import { useTRPC } from '@/utils/trpc';
import {
  AddEncounterForm,
  ENCOUNTER_CLASSES,
  type AddEncounterFormValues,
  type PractitionerOption,
} from '@liza/ui/components/records';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { Spinner } from '@liza/ui/components/ui/spinner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

type Props = {
  medplumPatientId: string;
  organizationId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AddEncounterDialog = ({
  medplumPatientId,
  organizationId,
  isOpen,
  onOpenChange,
}: Props): React.ReactNode => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  // Load the first page (20) of practitioners for the Provider dropdown. If we ever need to
  // support orgs with more than 20 active providers, switch the field to a searchable combobox
  // that calls `practitioner.list` with the `term` search parameter on each keystroke rather
  // than bumping `pageSize`.
  const practitionersQuery = useQuery({
    ...trpc.practitioner.list.queryOptions({ organizationId, pageSize: 20 }),
    enabled: isOpen,
  });

  const meQuery = useQuery({ ...trpc.user.me.queryOptions(), enabled: isOpen });

  const { mutateAsync: createEncounter, isPending: isCreating } = useMutation(
    trpc.patient.encounter.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.encounter.list.queryKey({ medplumPatientId }),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.documentReference.list.queryKey({ medplumPatientId }),
        });
      },
    }),
  );

  const practitioners = useMemo<PractitionerOption[]>(
    () => practitionersQuery.data?.practitioners.map((p) => ({ id: p.id, name: p.name })) ?? [],
    [practitionersQuery.data],
  );

  const handleCreate = async (values: AddEncounterFormValues): Promise<void> => {
    const classCode = ENCOUNTER_CLASSES.find((c) => c.code === values.classCode);
    if (!classCode) {
      toast.error(t('addEncounterFailed'));
      return;
    }
    const practitioner = practitioners.find((p) => p.id === values.practitionerId);
    try {
      await createEncounter({
        medplumPatientId,
        note: values.note,
        reasonText: values.reasonText,
        class: classCode,
        practitionerId: values.practitionerId,
        practitionerDisplay: practitioner?.name,
        start: new Date(values.start).toISOString(),
      });
      toast.success(t('encounterAdded'));
      onOpenChange(false);
    } catch (error) {
      toast.error(`${t('addEncounterFailed')}: ${error instanceof Error ? error.message : ''}`);
      throw error;
    }
  };

  const isReady = practitionersQuery.isSuccess && meQuery.isSuccess;
  const defaultPractitionerId = meQuery.data?.user.practitionerId || practitioners[0]?.id || '';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('addEncounter')}</DialogTitle>
        </DialogHeader>
        {isReady ? (
          <AddEncounterForm
            isCreating={isCreating}
            onClose={() => onOpenChange(false)}
            onCreate={handleCreate}
            practitioners={practitioners}
            defaultPractitionerId={defaultPractitionerId}
          />
        ) : (
          <div className="flex justify-center p-6">
            <Spinner className="size-6" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
